import { ReactElement, useLayoutEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import { ISegmentedControlProps } from './segmented-control.interface';
import styles from './segmented-control.module.scss';

const SegmentedControl = <T extends string>(
  props: ISegmentedControlProps<T>,
): ReactElement => {
  const { value, options, onChange, ariaLabel, className } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const measure = (): void => {
      const selected = container.querySelector<HTMLButtonElement>(
        'button[aria-pressed="true"]',
      );
      const next = selected
        ? {
            left: selected.offsetLeft,
            top: selected.offsetTop,
            width: selected.offsetWidth,
            height: selected.offsetHeight,
          }
        : null;
      setIndicator(previous =>
        previous?.left === next?.left &&
        previous?.top === next?.top &&
        previous?.width === next?.width &&
        previous?.height === next?.height
          ? previous
          : next,
      );
    };

    measure();
    // Follow font loading, label changes, and responsive layout without
    // imposing equal widths on the segments.
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(container);
    container.querySelectorAll('button').forEach(button => observer?.observe(button));
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [value, options]);

  return (
    <div
      ref={containerRef}
      className={classNames(styles.segmentedControl, className)}
      role="group"
      aria-label={ariaLabel}
    >
      {indicator && (
        <span
          aria-hidden="true"
          className={styles.indicator}
          style={{
            transform: `translateX(${indicator.left}px)`,
            top: indicator.top,
            width: indicator.width,
            height: indicator.height,
          }}
        />
      )}
      {options.map(option => {
        const isActive = option.value === value;

        return (
          <button
            key={option.value}
            type="button"
            className={classNames(styles.option, {
              [styles['option--active']]: isActive,
            })}
            onClick={() => onChange(option.value)}
            aria-pressed={isActive}
            aria-label={option.ariaLabel}
            title={option.title ?? option.ariaLabel}
            disabled={option.disabled}
          >
            <span className={styles.optionContent}>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
};

export { SegmentedControl };
